import { Box, Theme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Modals } from '@/constants';
import {
  selectIsPlayerPlaying,
  selectPlayerProgress,
  selectSessionUserId,
  setMediaSessionData,
  uiActions,
  useAppDispatch,
} from '@/store';
import { CPACanadaEpisode } from '@/types';

import { AudioVideoPlayer, MiniPlayer } from './components';

interface CPAPlayerProps {
  cpaCanadaContent?: CPACanadaEpisode | null;
}

export const CPAPlayer = ({ cpaCanadaContent }: CPAPlayerProps) => {
  const dispatch = useAppDispatch();
  const [episodeStarted, setStarted] = useState(false);
  const [contentEnded, setContentEnded] = useState(false);
  const [timer, setTimer] = useState<NodeJS.Timeout>();

  const isPlaying = useSelector(selectIsPlayerPlaying);
  const { position } = useSelector(selectPlayerProgress);
  const isAnonymous = !useSelector(selectSessionUserId);
  const cpaModalTimerDelay = isAnonymous ? 1 * 60 * 1000 : 8 * 1000;

  useEffect(() => {
    const { episode } = cpaCanadaContent || {};

    if (episode && isPlaying && !episodeStarted) {
      setStarted(true);

      dispatch(
        setMediaSessionData({
          title: episode.name,
          artist: 'LumiQ',
          album: episode.name,
          artwork: [
            {
              src: episode.cover,
              type: 'image/jpeg',
            },
          ],
        }),
      );
    }
  }, [isPlaying, cpaCanadaContent, episodeStarted, dispatch]);

  useEffect(() => {
    if (isPlaying && !timer) {
      setTimer(
        setTimeout(() => {
          dispatch(
            uiActions.setActiveModal({
              name: Modals.CPA_SIGNUP,
            }),
          );
        }, cpaModalTimerDelay),
      );
    }

    return () => {
      clearTimeout(timer);
    };
  }, [isPlaying, cpaModalTimerDelay, dispatch, timer]);

  useEffect(() => {
    if (
      !contentEnded &&
      isAnonymous &&
      (cpaCanadaContent?.episode?.duration ?? 0) - position! < 2
    ) {
      setContentEnded(true);
      dispatch(
        uiActions.setActiveModal({
          name: Modals.CPA_CONTENT_END,
        }),
      );
    }
  }, [dispatch, isAnonymous, cpaCanadaContent, position, contentEnded]);

  return (
    <>
      <Box sx={[styles.backdrop]} />
      <Box sx={styles.container}>
        <Box sx={styles.audioPlayer}>
          <AudioVideoPlayer
            playerStreamUrl={cpaCanadaContent?.episode.contentLink}
          />
        </Box>
        {!!cpaCanadaContent && (
          <MiniPlayer cpaCanadaContent={cpaCanadaContent} />
        )}
      </Box>
    </>
  );
};

const styles = {
  backdrop: {
    backgroundColor: 'blueBlack',
    opacity: 0.8,
    display: 'none',
    height: '100%',
    left: 0,
    position: 'fixed',
    top: 0,
    width: '100%',
    zIndex: 'player',
  },
  backdropExpanded: {
    display: 'block',
  },
  container: {
    bottom: 0,
    left: 0,
    position: 'fixed',
    width: '100%',
    zIndex: 'player',
  },
  audioPlayer: {
    height: 0,
    width: 0,
  },
  playerContainer: (theme: Theme) => ({
    backgroundColor: 'blueBlack',
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    bottom: 0,
    boxShadow: '0 -5px 20px 0 rgba(0,0,0,0.5)',
    height: 0,
    padding: 0,
    position: 'relative',
    width: '100%',
    zIndex: -1, // render behind the parent container that is placed at index 1300
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  }),
  playerContainerExpanded: (theme: Theme) => ({
    height: 'auto',
    padding: '50px 40px',
    [theme.breakpoints.down('sm')]: {
      padding: '30px 10px',
    },
  }),
};
